import React, { useState } from 'react';
import { ChevronDown, KeyRound, Link2, LogOut, Unlink } from 'lucide-react';
import { toast } from 'sonner';
import { commsApi } from '@/utils/commsApi';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SetPasswordDialog } from '@/components/SetPasswordDialog';
import { ChangePasswordDialog } from '@/components/ChangePasswordDialog';

const GoogleMark = () => (
  <svg width="14" height="14" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.5 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.2C12.4 13.6 17.7 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-2.8-.4-4.1H24v7.8h12.7c-.3 2.1-1.7 5.3-4.8 7.4l7.4 5.7c4.4-4.1 7.2-10.1 7.2-16.8z"/>
    <path fill="#FBBC05" d="M10.5 28.6A14.5 14.5 0 0 1 9.7 24c0-1.6.3-3.2.8-4.6l-7.9-6.2A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.8l7.9-6.2z"/>
    <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.4-5.7c-2 1.4-4.7 2.4-8.5 2.4-6.3 0-11.6-4.1-13.5-9.9l-7.9 6.2C6.5 42.6 14.6 48 24 48z"/>
  </svg>
);

export const startGoogleLink = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + '/dashboard/link-google';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

// Avatar dropdown: Google account connection, password setup (Google-only accounts) + logout.
export const ProfileMenu = ({ user, setUser, onLogout }) => {
  const [busy, setBusy] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);

  const disconnect = async () => {
    setBusy(true);
    try {
      const { user: updated } = await commsApi.unlinkGoogle();
      setUser(updated);
      toast.success('Google account disconnected');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not disconnect Google');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className="user-profile profile-trigger" data-testid="profile-menu-button">
            <img src={user.picture} alt={user.name} className="profile-avatar" />
            <span className="profile-name">{user.name}</span>
            <ChevronDown size={16} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="profile-menu" data-testid="profile-menu">
          <DropdownMenuLabel>
            <div className="profile-menu-name">{user.name}</div>
            <div className="profile-menu-email">{user.email}</div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {user.google_email ? (
            <>
              <DropdownMenuLabel className="profile-menu-google" data-testid="google-connected-label">
                <GoogleMark /> Google connected · {user.google_email}
              </DropdownMenuLabel>
              {user.has_password && (
                <DropdownMenuItem onSelect={disconnect} disabled={busy} data-testid="disconnect-google-button">
                  <Unlink size={16} /> Disconnect Google
                </DropdownMenuItem>
              )}
            </>
          ) : (
            <DropdownMenuItem onSelect={startGoogleLink} data-testid="connect-google-button">
              <Link2 size={16} /> Connect Google account
            </DropdownMenuItem>
          )}
          {!user.has_password && (
            <DropdownMenuItem onSelect={() => setPasswordOpen(true)} data-testid="set-password-button">
              <KeyRound size={16} /> Set a password
            </DropdownMenuItem>
          )}
          {user.has_password && (
            <DropdownMenuItem onSelect={() => setChangeOpen(true)} data-testid="change-password-button">
              <KeyRound size={16} /> Change password
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={onLogout} data-testid="logout-button">
            <LogOut size={16} /> Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <SetPasswordDialog open={passwordOpen} onOpenChange={setPasswordOpen} user={user} onSaved={setUser} />
      <ChangePasswordDialog open={changeOpen} onOpenChange={setChangeOpen} user={user} onSaved={setUser} />
    </>
  );
};
